import { fetchWithAuth } from "@/services/api.client"
import { parseSSEEvents, type StreamEvent } from "@/features/nira/sse"

export type { StreamEvent }

/** Events after which a turn stream is complete (no resume needed). */
export const TERMINAL_EVENTS: ReadonlySet<string> = new Set(["answer.done", "clarify", "turn.error"])

export type StreamEnd = "terminal" | "eof" | "no_content"
export interface StreamResult { ended: StreamEnd; lastSeq: number }

/** Non-2xx response from a NIRA route; `status` lets the UI tell 401/404/409/422 apart. */
export class NiraHttpError extends Error {
  constructor(public readonly status: number, public readonly detail: string, public readonly requestId?: string, public readonly code?: string, public readonly body?: unknown) {
    super(detail || `HTTP ${status}`)
    this.name = "NiraHttpError"
  }
}

async function httpError(res: Response): Promise<NiraHttpError> {
  let detail = ""
  let code: string | undefined
  let data: unknown
  try {
    const body: unknown = await res.json()
    const c = body && typeof body === "object" ? (body as { code?: unknown }).code : undefined
    if (typeof c === "string") code = c
    const d = body && typeof body === "object" ? (body as { detail?: unknown }).detail : undefined
    if (typeof d === "string") detail = d
    else if (d !== undefined) detail = JSON.stringify(d)
    // A structured refusal ({detail: {code, message, ...}}): its code and body, for the page to act on.
    if (d && typeof d === "object") {
      const inner = d as { code?: unknown; message?: unknown }
      if (typeof inner.code === "string") code = inner.code
      if (typeof inner.message === "string") detail = inner.message
      data = d
    }
  } catch {
    detail = res.statusText
  }
  return new NiraHttpError(res.status, detail, res.headers?.get?.("X-Request-Id") || undefined, code, data)
}

/** Fetch an SSE route and hand each parsed frame to `onEvent`. A 204 means nothing to replay. */
export async function streamEvents(
  url: string,
  init: RequestInit,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<StreamResult> {
  const res = await fetchWithAuth(url, { ...init, signal, credentials: "include" })
  if (res.status === 204) return { ended: "no_content", lastSeq: -1 }
  if (!res.ok) throw await httpError(res)
  if (!res.body) return { ended: "eof", lastSeq: -1 }
  let lastSeq = -1
  let terminal = false
  for await (const event of parseSSEEvents(res.body)) {
    const seq = event.payload.seq
    if (typeof seq === "number") lastSeq = Math.max(lastSeq, seq)
    if (TERMINAL_EVENTS.has(event.type)) terminal = true
    onEvent(event)
  }
  return { ended: terminal ? "terminal" : "eof", lastSeq }
}
